// Creación & Administración de Cuentas — EXCLUSIVO del administrador global
// (system_admin). Gestiona las cuentas (tenants) de la plataforma:
//   - Crear un propietario nuevo con su propia colección (tenant).
//   - Editar el nombre de una cuenta de propietario.
//   - Cambiar el PLAN de la cuenta.
//   - Ver los USUARIOS/INVITACIONES del tenant con su estado (pendiente/activo).
//   - ELIMINAR por completo una cuenta (borra el tenant entero — irreversible).

import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { useApi } from '../api/ApiContext.js';
import { useAuth } from '../auth/AuthContext.js';
import { AlertMessage, AsyncBoundary, Badge, ButtonSpinner, Card, Icon, PageHeader, Spinner } from '../ui/components.js';
import { useAsync } from '../ui/useAsync.js';
import { downloadBase64 } from '../ui/download.js';
import type { Api } from '../api/endpoints.js';
import type { AdminTenantBilling, OwnerAccount, Plan, TenantUserAccount } from '../types.js';

export function AccountsAdminView() {
  const { t } = useTranslation();
  const api = useApi();
  const { permissions } = useAuth();
  // Lista GLOBAL de cuentas (todos los tenants) — solo el administrador global.
  const accounts = useAsync(() => api.admin.listAccounts(), []);
  // Catálogo de planes (para el selector de cambio de plan).
  const plans = useAsync(() => api.admin.listPlans(), []);

  const [ownerEmail, setOwnerEmail] = useState('');
  const [ownerTenant, setOwnerTenant] = useState('');
  const [ownerPlan, setOwnerPlan] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  // --- Filtros del listado de cuentas (solo cliente, sobre la lista cargada) ---
  // Búsqueda de texto libre (email, nombre visible o nombre del catálogo),
  // más filtros por rol, plan y estado (activo/inactivo).
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState<'all' | 'owner' | 'system_admin'>('all');
  const [planFilter, setPlanFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');

  // La propia cuenta se identifica por email (MePermissions no expone el id).
  const currentEmail = permissions?.email?.toLowerCase() ?? null;
  const planList: Plan[] = plans.data?.items ?? [];

  const accountList: OwnerAccount[] = accounts.data?.items ?? [];

  // Planes presentes entre las cuentas cargadas (para poblar el selector de
  // filtro aunque algún plan ya no exista en el catálogo activo).
  const planOptions = useMemo(() => {
    const seen = new Map<string, string>();
    for (const a of accountList) {
      if (a.plan_code && !seen.has(a.plan_code)) {
        seen.set(a.plan_code, a.plan_name ?? a.plan_code);
      }
    }
    return [...seen.entries()].map(([code, name]) => ({ code, name }));
  }, [accountList]);

  // Lista filtrada que se renderiza. Combina todos los filtros con AND.
  const filteredAccounts = useMemo(() => {
    const q = search.trim().toLowerCase();
    return accountList.filter((a) => {
      if (q) {
        const haystack = [a.email, a.display_name ?? '', a.tenant_name ?? '']
          .join(' ')
          .toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      if (roleFilter !== 'all' && a.role !== roleFilter) return false;
      if (planFilter !== 'all' && (a.plan_code ?? '') !== planFilter) return false;
      if (statusFilter === 'active' && !a.active) return false;
      if (statusFilter === 'inactive' && a.active) return false;
      return true;
    });
  }, [accountList, search, roleFilter, planFilter, statusFilter]);

  const hasActiveFilters =
    search.trim() !== '' || roleFilter !== 'all' || planFilter !== 'all' || statusFilter !== 'all';

  function clearFilters() {
    setSearch('');
    setRoleFilter('all');
    setPlanFilter('all');
    setStatusFilter('all');
  }

  // Preselecciona el plan por defecto (`is_default`) en el formulario de crear
  // propietario una vez que cargan los planes (sin pisar una elección manual).
  useEffect(() => {
    if (ownerPlan === '' && planList.length > 0) {
      const def = planList.find((p) => p.is_default) ?? planList[0];
      if (def) setOwnerPlan(def.code);
    }
  }, [planList, ownerPlan]);

  async function createOwner() {
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      await api.admin.createOwner({
        email: ownerEmail.trim(),
        tenant_name: ownerTenant.trim() || undefined,
        plan_code: ownerPlan || undefined,
      });
      setMessage(t('accountsAdmin.ownerCreated', { email: ownerEmail }));
      setOwnerEmail('');
      setOwnerTenant('');
      accounts.reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('accountsAdmin.errCreateOwner'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex flex-col">
      <PageHeader title={t('accountsAdmin.pageTitle')} subtitle={t('accountsAdmin.pageSubtitle')} back />

      <div className="flex flex-col px-margin-mobile py-space-md gap-space-md">
        <AlertMessage message={error} />
        {message && (
          <div className="p-space-sm rounded-lg bg-goldmine-mint/15 border border-goldmine-mint/40 text-goldmine-mint font-body-sm text-body-sm">
            {message}
          </div>
        )}

        <Card title={t('accountsAdmin.createOwnerTitle')} icon="shield_person">
          <p className="font-body-sm text-body-sm text-on-surface-variant mb-space-sm">
            {t('accountsAdmin.createOwnerHint')}
          </p>
          <div className="flex flex-col gap-space-sm">
            <div className="field-slot">
              <Icon name="mail" className="text-on-surface-variant text-[18px] mr-space-xs" />
              <input
                className="field-input"
                type="email"
                value={ownerEmail}
                onChange={(e) => setOwnerEmail(e.target.value)}
                placeholder={t('accountsAdmin.ownerEmailPlaceholder')}
              />
            </div>
            <div className="field-slot">
              <Icon name="library_music" className="text-on-surface-variant text-[18px] mr-space-xs" />
              <input
                className="field-input"
                value={ownerTenant}
                onChange={(e) => setOwnerTenant(e.target.value)}
                placeholder={t('accountsAdmin.ownerTenantPlaceholder')}
              />
            </div>
            {/* Plan inicial de la cuenta (preseleccionado con el plan por defecto). */}
            <div className="field-slot">
              <Icon name="workspace_premium" className="text-on-surface-variant text-[18px] mr-space-xs" />
              <select
                className="field-input"
                value={ownerPlan}
                onChange={(e) => setOwnerPlan(e.target.value)}
                disabled={planList.length === 0}
                aria-label={t('accountsAdmin.initialPlanLabel')}
              >
                {planList.length === 0 && <option value="">{t('accountsAdmin.loadingPlans')}</option>}
                {planList.map((p) => (
                  <option key={p.code} value={p.code}>
                    {p.name}
                    {p.is_default ? t('accountsAdmin.planDefaultSuffix') : ''}
                  </option>
                ))}
              </select>
            </div>
            <button
              className="btn-primary w-full"
              type="button"
              disabled={busy || !ownerEmail.trim()}
              onClick={() => void createOwner()}
            >
              {busy ? <ButtonSpinner /> : <Icon name="add_moderator" className="text-[18px]" />}{' '}
              {t('accountsAdmin.createOwnerButton')}
            </button>
          </div>
        </Card>

        {/* Cuentas de propietario de TODOS los tenants (vista global del admin). */}
        <span className="section-label text-on-surface-variant">{t('accountsAdmin.ownerAccountsLabel')}</span>

        {/* Panel de filtros: búsqueda de texto + rol + plan + estado. Filtra
            sobre la lista ya cargada (sin llamadas extra al backend). */}
        <Card title={t('accountsAdmin.filtersTitle')} icon="filter_list">
          <div className="flex flex-col gap-space-sm">
            {/* Búsqueda libre: email, nombre visible o nombre del catálogo. */}
            <div className="field-slot">
              <Icon name="search" className="text-on-surface-variant text-[18px] mr-space-xs" />
              <input
                className="field-input"
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t('accountsAdmin.searchPlaceholder')}
                aria-label={t('accountsAdmin.searchAria')}
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-space-sm">
              {/* Filtro por rol. */}
              <div className="field-slot">
                <Icon name="badge" className="text-on-surface-variant text-[18px] mr-space-xs" />
                <select
                  className="field-input"
                  value={roleFilter}
                  onChange={(e) => setRoleFilter(e.target.value as typeof roleFilter)}
                  aria-label={t('accountsAdmin.roleFilterAria')}
                >
                  <option value="all">{t('accountsAdmin.roleAll')}</option>
                  <option value="owner">{t('accountsAdmin.roleOwner')}</option>
                  <option value="system_admin">{t('accountsAdmin.roleSystemAdmin')}</option>
                </select>
              </div>

              {/* Filtro por plan. */}
              <div className="field-slot">
                <Icon name="workspace_premium" className="text-on-surface-variant text-[18px] mr-space-xs" />
                <select
                  className="field-input"
                  value={planFilter}
                  onChange={(e) => setPlanFilter(e.target.value)}
                  aria-label={t('accountsAdmin.planFilterAria')}
                >
                  <option value="all">{t('accountsAdmin.planAll')}</option>
                  <option value="">{t('accountsAdmin.planNone')}</option>
                  {planOptions.map((p) => (
                    <option key={p.code} value={p.code}>
                      {p.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Filtro por estado (activo / inactivo). */}
              <div className="field-slot">
                <Icon name="toggle_on" className="text-on-surface-variant text-[18px] mr-space-xs" />
                <select
                  className="field-input"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value as typeof statusFilter)}
                  aria-label={t('accountsAdmin.statusFilterAria')}
                >
                  <option value="all">{t('accountsAdmin.statusAny')}</option>
                  <option value="active">{t('accountsAdmin.statusActivePlural')}</option>
                  <option value="inactive">{t('accountsAdmin.statusInactivePlural')}</option>
                </select>
              </div>
            </div>

            {/* Resumen de resultados + limpiar filtros. */}
            <div className="flex items-center justify-between gap-space-sm">
              <span className="font-body-sm text-body-sm text-on-surface-variant">
                {t('accountsAdmin.resultCount', {
                  shown: filteredAccounts.length,
                  total: accountList.length,
                  noun:
                    accountList.length === 1
                      ? t('accountsAdmin.accountSingular')
                      : t('accountsAdmin.accountPlural'),
                })}
              </span>
              {hasActiveFilters && (
                <button className="btn-ghost btn-sm" type="button" onClick={clearFilters}>
                  <Icon name="filter_list_off" className="text-[18px]" /> {t('accountsAdmin.clearFilters')}
                </button>
              )}
            </div>
          </div>
        </Card>

        <AsyncBoundary state={accounts} empty={(d) => d.items.length === 0}>
          {() =>
            filteredAccounts.length === 0 ? (
              <p className="font-body-sm text-body-sm text-on-surface-variant surface-rack-card !p-space-sm text-center">
                {t('accountsAdmin.noMatches')}
              </p>
            ) : (
              <div className="flex flex-col gap-space-sm">
                {filteredAccounts.map((account) => (
                  <AccountCard
                    key={account.id}
                    api={api}
                    account={account}
                    plans={planList}
                    isSelf={currentEmail !== null && account.email.toLowerCase() === currentEmail}
                    onChanged={() => accounts.reload()}
                    onError={setError}
                    onMessage={setMessage}
                  />
                ))}
              </div>
            )
          }
        </AsyncBoundary>
      </div>
    </div>
  );
}

/** Etiqueta y tono del badge de estado de un usuario/invitación. */
function statusBadge(t: TFunction, status: TenantUserAccount['status']) {
  switch (status) {
    case 'active':
      return { tone: 'success' as const, label: t('accountsAdmin.userStatusActive') };
    case 'pending':
      return { tone: 'warn' as const, label: t('accountsAdmin.userStatusPending') };
    case 'revoked':
      return { tone: 'danger' as const, label: t('accountsAdmin.userStatusRevoked') };
    default:
      return { tone: 'neutral' as const, label: t('accountsAdmin.userStatusUnknown') };
  }
}

/** Tarjeta de una cuenta: nombre, plan, usuarios y acciones. */
function AccountCard({
  api,
  account,
  plans,
  isSelf,
  onChanged,
  onError,
  onMessage,
}: {
  api: Api;
  account: OwnerAccount;
  plans: Plan[];
  isSelf: boolean;
  onChanged: () => void;
  onError: (m: string | null) => void;
  onMessage: (m: string | null) => void;
}) {
  const { t } = useTranslation();
  const isSystem = account.role === 'system_admin';

  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState(account.display_name ?? '');
  const [editTenant, setEditTenant] = useState(account.tenant_name ?? '');
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [changingPlan, setChangingPlan] = useState(false);

  // Usuarios del tenant (carga perezosa al expandir).
  const [showUsers, setShowUsers] = useState(false);
  const [users, setUsers] = useState<TenantUserAccount[] | null>(null);
  const [loadingUsers, setLoadingUsers] = useState(false);

  // Facturación del tenant (carga perezosa al expandir).
  const [showBilling, setShowBilling] = useState(false);
  const [billing, setBilling] = useState<AdminTenantBilling | null>(null);
  const [loadingBilling, setLoadingBilling] = useState(false);

  async function saveName() {
    setSaving(true);
    onError(null);
    onMessage(null);
    try {
      // Guarda solo lo que cambió: el nombre visible del owner y/o el nombre del
      // catálogo (tenant). El admin de sistema no tiene catálogo editable aquí.
      const nameChanged = (editName.trim() || null) !== (account.display_name ?? null);
      const tenantChanged = !isSystem && editTenant.trim() !== '' && editTenant.trim() !== (account.tenant_name ?? '');
      if (nameChanged) {
        await api.admin.updateUser(account.id, { display_name: editName.trim() || null });
      }
      if (tenantChanged) {
        await api.admin.renameTenant(account.tenant_id, editTenant.trim());
      }
      onMessage(t('accountsAdmin.accountUpdated', { email: account.email }));
      setEditing(false);
      onChanged();
    } catch (err) {
      onError(err instanceof Error ? err.message : t('accountsAdmin.errUpdateAccount'));
    } finally {
      setSaving(false);
    }
  }

  async function changePlan(planCode: string) {
    if (planCode === (account.plan_code ?? '')) return;
    setChangingPlan(true);
    onError(null);
    onMessage(null);
    try {
      await api.admin.setTenantPlan(account.tenant_id, planCode);
      onMessage(t('accountsAdmin.planUpdatedFor', { tenant: account.tenant_name }));
      onChanged();
    } catch (err) {
      onError(err instanceof Error ? err.message : t('accountsAdmin.errChangePlan'));
    } finally {
      setChangingPlan(false);
    }
  }

  async function toggleUsers() {
    const next = !showUsers;
    setShowUsers(next);
    if (next && users === null && !loadingUsers) {
      setLoadingUsers(true);
      try {
        const res = await api.admin.listTenantUsers(account.tenant_id);
        setUsers(res.items);
      } catch (err) {
        onError(err instanceof Error ? err.message : t('accountsAdmin.errLoadUsers'));
        setShowUsers(false);
      } finally {
        setLoadingUsers(false);
      }
    }
  }

  async function toggleBilling() {
    const next = !showBilling;
    setShowBilling(next);
    if (next && billing === null && !loadingBilling) {
      setLoadingBilling(true);
      try {
        const res = await api.admin.getTenantBilling(account.tenant_id);
        setBilling(res);
      } catch (err) {
        onError(err instanceof Error ? err.message : t('accountsAdmin.errLoadBilling'));
        setShowBilling(false);
      } finally {
        setLoadingBilling(false);
      }
    }
  }

  async function downloadConsentReceipt() {
    onError(null);
    try {
      const receipt = await api.admin.consentReceipt(account.email);
      downloadBase64(receipt.base64, receipt.filename, receipt.content_type);
    } catch (err) {
      onError(err instanceof Error ? err.message : t('common.error'));
    }
  }

  async function downloadDataExport() {
    onError(null);
    try {
      const pkg = await api.admin.dataExport(account.id);
      downloadBase64(pkg.base64, pkg.filename, pkg.content_type);
    } catch (err) {
      onError(err instanceof Error ? err.message : t('common.error'));
    }
  }

  async function deleteOwner() {
    const typed = prompt(t('accountsAdmin.deletePrompt', { email: account.email }));
    if (typed === null) return;
    if (typed.trim().toLowerCase() !== account.email.toLowerCase()) {
      onError(t('accountsAdmin.deleteEmailMismatch'));
      return;
    }
    setDeleting(true);
    onError(null);
    onMessage(null);
    try {
      await api.admin.deleteOwner(account.id);
      onMessage(t('accountsAdmin.accountDeleted', { email: account.email }));
      onChanged();
    } catch (err) {
      onError(err instanceof Error ? err.message : t('accountsAdmin.errDeleteAccount'));
    } finally {
      setDeleting(false);
    }
  }

  return (
    <Card
      title={account.display_name ?? account.email}
      icon={isSystem ? 'shield_person' : 'library_music'}
      action={
        isSystem ? (
          <Badge tone="info">{t('accountsAdmin.badgeSystemAdmin')}</Badge>
        ) : (
          <Badge tone="info">{t('accountsAdmin.badgeOwner')}</Badge>
        )
      }
    >
      <p className="font-code-catalog text-body-sm text-on-surface-variant">{account.email}</p>
      <p className="flex items-center gap-space-xs font-body-sm text-body-sm text-on-surface-variant mb-space-sm">
        <Icon name="library_music" className="text-[16px]" /> {account.tenant_name}
      </p>

      {/* Plan de la cuenta: muestra el actual y permite cambiarlo. El admin de
          sistema no tiene un plan de cliente editable aquí. */}
      {!isSystem && (
        <div className="flex items-center justify-between gap-space-sm surface-rack-card !p-space-sm mb-space-sm">
          <span className="flex items-center gap-space-xs font-body-sm text-body-sm text-on-surface">
            <Icon name="workspace_premium" className="text-primary text-[18px]" /> {t('accountsAdmin.planLabel')}
          </span>
          <div className="flex items-center gap-space-xs">
            {changingPlan && <ButtonSpinner />}
            <select
              className="field-input !h-9 !w-auto"
              value={account.plan_code ?? ''}
              disabled={changingPlan || plans.length === 0}
              onChange={(e) => void changePlan(e.target.value)}
            >
              {account.plan_code === null && <option value="">{t('accountsAdmin.planNoneOption')}</option>}
              {plans.map((p) => (
                <option key={p.code} value={p.code}>
                  {p.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      {/* Usuarios / invitaciones del tenant (carga perezosa). */}
      {!isSystem && (
        <div className="mb-space-sm">
          <button
            type="button"
            className="w-full flex items-center justify-between gap-space-sm surface-rack-card !p-space-sm"
            onClick={() => void toggleUsers()}
            aria-expanded={showUsers}
          >
            <span className="flex items-center gap-space-xs font-body-sm text-body-sm text-on-surface">
              <Icon name="group" className="text-primary text-[18px]" /> {t('accountsAdmin.usersToggle')}
            </span>
            <Icon
              name="expand_more"
              className={`text-on-surface-variant text-[22px] transition-transform ${showUsers ? 'rotate-180' : ''}`}
            />
          </button>
          {showUsers && (
            <div className="mt-space-xs flex flex-col gap-space-xs">
              {loadingUsers && <Spinner label={t('accountsAdmin.loadingUsers')} />}
              {!loadingUsers && users !== null && users.length === 0 && (
                <p className="font-body-sm text-body-sm text-on-surface-variant px-space-xs">
                  {t('accountsAdmin.noExtraUsers')}
                </p>
              )}
              {!loadingUsers &&
                users?.map((u) => {
                  const b = statusBadge(t, u.status);
                  return (
                    <div
                      key={u.id}
                      className="flex items-center justify-between gap-space-sm surface-rack-card !p-space-xs !py-space-sm"
                    >
                      <span className="flex flex-col min-w-0">
                        <span className="font-body-sm text-body-sm text-on-surface truncate">
                          {u.display_name ?? u.email}
                        </span>
                        <span className="font-code-catalog text-body-sm text-on-surface-variant truncate">
                          {u.email} ·{' '}
                          {u.role === 'owner'
                            ? t('accountsAdmin.memberRoleOwner')
                            : u.role === 'system_admin'
                              ? t('accountsAdmin.memberRoleAdmin')
                              : t('accountsAdmin.memberRoleMember')}
                        </span>
                      </span>
                      <Badge tone={b.tone}>{b.label}</Badge>
                    </div>
                  );
                })}
            </div>
          )}
        </div>
      )}

      {/* Facturación / transacción del plan (carga perezosa). */}
      {!isSystem && (
        <div className="mb-space-sm">
          <button
            type="button"
            className="w-full flex items-center justify-between gap-space-sm surface-rack-card !p-space-sm"
            onClick={() => void toggleBilling()}
            aria-expanded={showBilling}
          >
            <span className="flex items-center gap-space-xs font-body-sm text-body-sm text-on-surface">
              <Icon name="receipt_long" className="text-primary text-[18px]" /> {t('accountsAdmin.billingToggle')}
            </span>
            <Icon
              name="expand_more"
              className={`text-on-surface-variant text-[22px] transition-transform ${showBilling ? 'rotate-180' : ''}`}
            />
          </button>
          {showBilling && (
            <div className="mt-space-xs">
              {loadingBilling && <Spinner label={t('accountsAdmin.loadingBilling')} />}
              {!loadingBilling && billing !== null && <BillingDetail billing={billing} />}
            </div>
          )}
        </div>
      )}

      {editing ? (
        <div className="flex flex-col gap-space-sm">
          <div className="field-slot">
            <Icon name="badge" className="text-on-surface-variant text-[18px] mr-space-xs" />
            <input
              className="field-input"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              placeholder={t('accountsAdmin.visibleNamePlaceholder')}
            />
          </div>
          {/* Nombre del catálogo (tenant). El admin de sistema no tiene uno editable. */}
          {!isSystem && (
            <div className="field-slot">
              <Icon name="library_music" className="text-on-surface-variant text-[18px] mr-space-xs" />
              <input
                className="field-input"
                value={editTenant}
                onChange={(e) => setEditTenant(e.target.value)}
                placeholder={t('accountsAdmin.tenantNamePlaceholder')}
                aria-label={t('accountsAdmin.tenantNameAria')}
              />
            </div>
          )}
          <div className="flex items-center gap-space-sm">
            <button className="btn-primary flex-1" type="button" disabled={saving} onClick={() => void saveName()}>
              {saving ? <ButtonSpinner /> : <Icon name="check" className="text-[18px]" />} {t('accountsAdmin.save')}
            </button>
            <button className="btn-ghost" type="button" onClick={() => setEditing(false)}>
              <Icon name="close" className="text-[18px]" /> {t('accountsAdmin.cancel')}
            </button>
          </div>
        </div>
      ) : (
        <div className="flex items-center gap-space-sm">
          <button className="btn-secondary flex-1" type="button" onClick={() => setEditing(true)}>
            <Icon name="edit" className="text-[18px]" />{' '}
            {isSystem ? t('accountsAdmin.editName') : t('accountsAdmin.editAccount')}
          </button>
          {/* Comprobante de consentimiento del sujeto (PDF): prueba descargable. */}
          <button
            className="btn-ghost"
            type="button"
            title={t('accountsAdmin.downloadConsentReceipt')}
            aria-label={t('accountsAdmin.downloadConsentReceipt')}
            onClick={() => void downloadConsentReceipt()}
          >
            <Icon name="receipt_long" className="text-[18px]" />
          </button>
          {/* Export del paquete de datos de la cuenta (GDPR acceso/portabilidad). */}
          {!isSystem && (
            <button
              className="btn-ghost"
              type="button"
              title={t('accountsAdmin.downloadDataExport')}
              aria-label={t('accountsAdmin.downloadDataExport')}
              onClick={() => void downloadDataExport()}
            >
              <Icon name="download" className="text-[18px]" />
            </button>
          )}
          {/* No se permite borrar al admin de sistema ni la propia cuenta. */}
          {!isSystem && !isSelf && (
            <button
              className="btn-ghost !text-priority-urgent"
              type="button"
              disabled={deleting}
              onClick={() => void deleteOwner()}
            >
              {deleting ? <ButtonSpinner /> : <Icon name="delete_forever" className="text-[18px]" />}{' '}
              {t('accountsAdmin.deleteAccount')}
            </button>
          )}
        </div>
      )}
    </Card>
  );
}

/** Formatea una fecha ISO a texto local legible (o "—"). */
function fmtDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString();
}

/** Formatea fecha + hora para el ledger de eventos. */
function fmtDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString();
}

/** Formatea centavos a moneda legible (p. ej. 1500, "usd" → "$15.00 USD"). */
function fmtMoney(cents: number, currency: string): string {
  const amount = (cents / 100).toFixed(2);
  const symbol = currency.toUpperCase() === 'USD' ? '$' : '';
  return `${symbol}${amount} ${currency.toUpperCase()}`;
}

/** Etiqueta + tono del estado de la suscripción. */
function subStatusBadge(t: TFunction, status: 'active' | 'past_due' | 'canceled' | 'trialing') {
  switch (status) {
    case 'active':
      return { tone: 'success' as const, label: t('accountsAdmin.subStatusActive') };
    case 'trialing':
      return { tone: 'info' as const, label: t('accountsAdmin.subStatusTrialing') };
    case 'past_due':
      return { tone: 'warn' as const, label: t('accountsAdmin.subStatusPastDue') };
    case 'canceled':
    default:
      return { tone: 'neutral' as const, label: t('accountsAdmin.subStatusCanceled') };
  }
}

/** Etiqueta legible del estado de una factura de Stripe. */
function invoiceStatusLabel(t: TFunction, status: string | null): string {
  switch (status) {
    case 'paid':
      return t('accountsAdmin.invoicePaid');
    case 'open':
      return t('accountsAdmin.invoiceOpen');
    case 'void':
      return t('accountsAdmin.invoiceVoid');
    case 'uncollectible':
      return t('accountsAdmin.invoiceUncollectible');
    case 'draft':
      return t('accountsAdmin.invoiceDraft');
    default:
      return status ?? t('accountsAdmin.invoiceUnknown');
  }
}

/** Fila etiqueta/valor para el detalle de la suscripción. */
function Row({ label, value, mono }: { label: string; value: ReactNode; mono?: boolean }) {
  return (
    <div className="flex items-start justify-between gap-space-sm py-space-xs">
      <span className="font-body-sm text-body-sm text-on-surface-variant shrink-0">{label}</span>
      <span className={`text-body-sm text-right break-all ${mono ? 'font-code-catalog' : 'font-body-sm'} text-on-surface`}>
        {value}
      </span>
    </div>
  );
}

/**
 * Detalle de facturación de una cuenta (vista del admin): suscripción con sus
 * fechas e IDs de Stripe (con enlaces al dashboard), historial de facturas (con
 * enlaces a la factura/PDF de Stripe) y el ledger de eventos de la transacción.
 */
function BillingDetail({ billing }: { billing: AdminTenantBilling }) {
  const { t } = useTranslation();
  const { subscription: sub, invoices, events, billing_configured } = billing;

  return (
    <div className="flex flex-col gap-space-sm mt-space-xs">
      {!billing_configured && (
        <p className="font-body-sm text-body-sm text-on-surface-variant surface-rack-card !p-space-sm">
          {t('accountsAdmin.billingNotConfigured')}
        </p>
      )}

      {/* Suscripción */}
      {sub ? (
        <div className="surface-rack-card !p-space-sm flex flex-col">
          <div className="flex items-center justify-between mb-space-xs">
            <span className="flex items-center gap-space-xs font-body-sm text-body-sm text-on-surface font-semibold">
              <Icon name="workspace_premium" className="text-primary text-[18px]" /> {t('accountsAdmin.subscriptionTitle')}
            </span>
            {(() => {
              const b = subStatusBadge(t, sub.status);
              return <Badge tone={b.tone}>{b.label}</Badge>;
            })()}
          </div>
          <div className="divide-y divide-outline-variant/30">
            <Row label={t('accountsAdmin.enrollDate')} value={fmtDate(sub.started_at)} />
            <Row
              label={t('accountsAdmin.cyclePeriod')}
              value={`${fmtDate(sub.current_period_start)} — ${fmtDate(sub.current_period_end)}`}
            />
            <Row
              label={sub.cancel_at_period_end ? t('accountsAdmin.cancelDate') : t('accountsAdmin.renewDate')}
              value={fmtDate(sub.current_period_end)}
            />
            <Row label={t('accountsAdmin.stripeCustomerId')} value={sub.stripe_customer_id ?? '—'} mono />
            <Row label={t('accountsAdmin.stripeSubscriptionId')} value={sub.stripe_subscription_id ?? '—'} mono />
            <Row label={t('accountsAdmin.stripePriceId')} value={sub.stripe_price_id ?? '—'} mono />
          </div>
          {(sub.stripe_customer_url || sub.stripe_subscription_url) && (
            <div className="flex flex-wrap items-center gap-space-xs mt-space-sm">
              {sub.stripe_customer_url && (
                <a href={sub.stripe_customer_url} target="_blank" rel="noopener noreferrer" className="btn-secondary btn-sm">
                  <Icon name="open_in_new" className="text-[16px]" /> {t('accountsAdmin.stripeCustomerLink')}
                </a>
              )}
              {sub.stripe_subscription_url && (
                <a
                  href={sub.stripe_subscription_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-secondary btn-sm"
                >
                  <Icon name="open_in_new" className="text-[16px]" /> {t('accountsAdmin.stripeSubscriptionLink')}
                </a>
              )}
            </div>
          )}
        </div>
      ) : (
        <p className="font-body-sm text-body-sm text-on-surface-variant surface-rack-card !p-space-sm">
          {t('accountsAdmin.noSubscription')}
        </p>
      )}

      {/* Facturas */}
      <div className="surface-rack-card !p-space-sm">
        <span className="flex items-center gap-space-xs font-body-sm text-body-sm text-on-surface font-semibold mb-space-xs">
          <Icon name="receipt" className="text-primary text-[18px]" /> {t('accountsAdmin.invoicesTitle')}
        </span>
        {invoices.length === 0 ? (
          <p className="font-body-sm text-body-sm text-on-surface-variant">{t('accountsAdmin.noInvoices')}</p>
        ) : (
          <div className="flex flex-col divide-y divide-outline-variant/30">
            {invoices.map((inv) => (
              <div key={inv.id} className="flex flex-col gap-space-xs py-space-sm first:pt-0 last:pb-0">
                <div className="flex items-center justify-between gap-space-sm">
                  <div className="flex flex-col min-w-0">
                    <span className="font-body-sm text-body-sm text-on-surface font-semibold truncate">
                      {inv.number ?? inv.id}
                    </span>
                    <span className="font-body-sm text-body-sm text-on-surface-variant">
                      {fmtDate(inv.created)} · {fmtMoney(inv.amount_cents, inv.currency)} ·{' '}
                      {invoiceStatusLabel(t, inv.status)}
                    </span>
                    <span className="font-code-catalog text-body-sm text-on-surface-variant truncate">
                      {t('accountsAdmin.paymentPrefix', { id: inv.id })}
                    </span>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-space-xs">
                  {inv.hosted_invoice_url && (
                    <a href={inv.hosted_invoice_url} target="_blank" rel="noopener noreferrer" className="btn-secondary btn-sm">
                      <Icon name="open_in_new" className="text-[16px]" /> {t('accountsAdmin.viewInStripe')}
                    </a>
                  )}
                  {inv.invoice_pdf && (
                    <a href={inv.invoice_pdf} target="_blank" rel="noopener noreferrer" className="btn-secondary btn-sm">
                      <Icon name="download" className="text-[16px]" /> {t('accountsAdmin.invoicePdf')}
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Ledger de eventos (rastro de la transacción) */}
      <div className="surface-rack-card !p-space-sm">
        <span className="flex items-center gap-space-xs font-body-sm text-body-sm text-on-surface font-semibold mb-space-xs">
          <Icon name="history" className="text-primary text-[18px]" /> {t('accountsAdmin.transactionHistoryTitle')}
        </span>
        {events.length === 0 ? (
          <p className="font-body-sm text-body-sm text-on-surface-variant">{t('accountsAdmin.noEvents')}</p>
        ) : (
          <div className="flex flex-col divide-y divide-outline-variant/30">
            {events.map((ev) => (
              <div key={ev.id} className="flex flex-col py-space-xs first:pt-0 last:pb-0">
                <div className="flex items-center justify-between gap-space-sm">
                  <span className="font-body-sm text-body-sm text-on-surface">{ev.summary ?? ev.action}</span>
                  <span className="font-body-sm text-body-sm text-on-surface-variant shrink-0">
                    {fmtDateTime(ev.created_at)}
                  </span>
                </div>
                <span className="font-code-catalog text-body-sm text-on-surface-variant">
                  {ev.action} · {ev.source}
                  {ev.stripe_event_id ? ` · ${t('accountsAdmin.eventIdPrefix', { id: ev.stripe_event_id })}` : ''}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
